<script lang="ts">
  import { page } from '$app/stores';
  import { Trophy, ChevronRight } from 'lucide-svelte';
  import { _, locale } from 'svelte-i18n';
  import { hasTranslation } from '$lib/i18n';
  import { Button } from '$lib/components/shadcn/ui/button';
  
  // 翻訳関数のラッパー
  function t(key: string, defaultValue: string, values?: Record<string, any>): string {
    // 現在の言語を取得
    let currentLocale = 'ja';
    locale.subscribe(value => {
      currentLocale = value || 'ja';
    })();
    
    // 翻訳キーが存在するか確認
    if (hasTranslation(key, currentLocale)) {
      return values ? $_(key, { values }) : $_(key);
    } else {
      return defaultValue;
    }
  }

  // カテゴリーをリアクティブに定義
  $: categories = [
    { id: 'all', name: t('category.all', 'すべて'), description: t('category.all_description', 'すべてのカテゴリーの配信を表示') },
    { id: 'ASMR', name: t('category.asmr', 'ASMR'), description: t('category.asmr_description', 'ASMR配信を表示') },
    { id: 'ゲーム', name: t('category.gaming', 'ゲーム'), description: t('category.gaming_description', 'ゲーム配信を表示') },
    { id: '雑談', name: t('category.talk', '雑談'), description: t('category.talk_description', '雑談配信を表示') },
    { id: '音楽', name: t('category.music', '音楽'), description: t('category.music_description', '音楽配信を表示') },
  ];

  let categoryLink = '';
  function getCategoryLink(categoryId: string) {
    switch (categoryId) {
      case 'all':
        categoryLink = '/';
        break;
      default:
        categoryLink = `/category/${categoryId}`;
        break;
    }
    return categoryLink;
  }

  $: activeCategory = $page.url.pathname.startsWith('/category/') 
    ? $page.params.id 
    : ($page.url.pathname === '/rankings' ? 'rankings' : 'all');
</script>

<nav class="category-menu mb-6" aria-label={t('category.label', 'カテゴリー')}>
  <div class="relative px-2">
    <!-- グラデーションのオーバーレイ -->
    <div class="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white dark:from-zinc-900 to-transparent z-10" />
    <div class="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white dark:from-zinc-900 to-transparent z-10" />
    
    <!-- スクロール可能なコンテナ -->
    <div class="overflow-x-auto no-scrollbar">
      <ul 
        class="flex items-center gap-2 py-1 min-w-min"
        role="tablist"
      >
        {#each categories as category (category.id)}
          <li class="flex-none">
            <Button
              href={getCategoryLink(category.id)}
              variant={activeCategory === category.id ? "default" : "outline"}
              size="sm"
              class="rounded-sm font-medium {activeCategory === category.id ? 'dark:text-white' : ''}"
              role="tab"
              aria-selected={activeCategory === category.id}
              aria-controls="category-content"
              id="category-tab-{category.id}"
              aria-label={category.description}
            >
              {category.name}
            </Button>
          </li>
        {/each}
      </ul>
    </div>
  </div>
</nav>

<style>
  .category-menu {
    position: relative;
  }

  /* スクロールバーを非表示にする */
  .no-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }

  /* アクティブ状態のスタイル */
  .active {
    color: white;
    background-color: theme('colors.neutral.900');
  }

  :global(.dark) .active {
    background-color: theme('colors.neutral.100');
    color: theme('colors.neutral.900');
  }

  /* 非アクティブ状態のスタイル */
  a:not(.active) {
    color: theme('colors.neutral.600');
    background-color: theme('colors.neutral.100');
  }

  /* ダークモード */
  :global(.dark) a:not(.active) {
    color: theme('colors.neutral.400');
    background-color: theme('colors.zinc.900/0.5');
  }

  /* ホバー状態 */
  a:not(.active):hover {
    color: theme('colors.neutral.900');
    background-color: theme('colors.neutral.200');
  }

  :global(.dark) a:not(.active):hover {
    color: theme('colors.white');
    background-color: theme('colors.zinc.900');
  }
</style>
